import React, { useRef, useEffect, useState, ReactNode } from 'react';
import styled from 'styled-components';

interface ScrollableWithIndicatorsProps {
  children: ReactNode;
  height?: number;
  iconColor?: string;
}

const Scrollable = styled.div`
  scrollbar-width: 'none';  
&::-webkit-scrollbar {
  display: none;
}
`
const ArrowUp: React.FC<{ color: string }> = ({ color }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    style={{
      animation: 'bounceUp 1.5s infinite',
    }}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 4L6 10H10V20H14V10H18L12 4Z"
      fill={color}
    />
  </svg>
);

const ArrowDown: React.FC<{ color: string }> = ({ color }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    style={{
      animation: 'bounceDown 1.5s infinite',
    }}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 20L18 14H14V4H10V14H6L12 20Z"
      fill={color}
    />
  </svg>
);

const ScrollableWithIndicators: React.FC<ScrollableWithIndicatorsProps> = ({
  children,
  iconColor = '#999',
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [showTopIndicator, setShowTopIndicator] = useState(false);
  const [showBottomIndicator, setShowBottomIndicator] = useState(false);

  const checkScroll = () => {
    const el = containerRef.current;
    if (!el) return;

    setShowTopIndicator(el.scrollTop > 0);
    setShowBottomIndicator(el.scrollTop + el.clientHeight < el.scrollHeight);
  };

  useEffect(() => {
    checkScroll(); // Initial check

    const el = containerRef.current;
    if (!el) return;

    el.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);

    return () => {
      el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, []);

  return (
    <Scrollable
      ref={containerRef}
      style={{
        position: 'relative',
        overflow: 'auto',
        paddingRight: '0.5rem',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      }}
      className="hide-scrollbar"
    >
      {showTopIndicator && (
        <div
          style={{
            position: 'sticky',
            top: 0,
            display: 'flex',
            justifyContent: 'center',
            zIndex: 1,
            background: 'linear-gradient(180deg, rgba(255,255,255,1) 40%, rgba(255,255,255,0.5) 100%)', // match your content bg
            pointerEvents: 'none',
            padding: '12px'
          }}
        >
          <ArrowUp color={iconColor} />
        </div>
      )}
      {children}

      {showBottomIndicator && (
        <div
          style={{
            position: 'sticky',
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            zIndex: 1,
            background: 'linear-gradient(180deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,1) 60%)', // match your content bg
            pointerEvents: 'none',
            padding: '12px'
          }}
        >
          <ArrowDown color={iconColor} />
        </div>
      )}
    </Scrollable>
  );
};

export default ScrollableWithIndicators;
